import { Avatar, Box, ButtonBase, Stack, Typography } from '@mui/material'
import Head from 'next/head'
import { useEffect, useState } from 'react'
import { Fade, IconButton } from 'utoniq-ui'

import {
  ItemImage,
  Maybe,
  ResourceFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Header } from '@/components/layouts'
import { FullscreenModal } from '@/components/organisms/functionals'
import { ImagePlayer } from '@/components/organisms/resourcePlayer/ImagePlayer'
import { styles, styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { Privacy } from '@/core/native'

export type ThumbnailFullScreenModalProps = {
  open: boolean
  /** 表示する画像のデータ */
  resource?: Maybe<ResourceFragment> | undefined
  /** 表裏があるリソースのデータ、アイテム詳細画面で用いる */
  thumbnail?: {
    front?: Maybe<ResourceFragment> | undefined
    back?: Maybe<ResourceFragment> | undefined
  }
  /** アイテムの表・裏どちらを表示しているかの状態 */
  isFlipped?: boolean
  onClose: () => void
  showMultipleImage?: boolean
  images?: Maybe<Pick<ItemImage, 'caption' | 'mediumUri'>[]>
  /**
   * DRMビュワーとして開くかどうか。trueの場合、DRM再生が可能だがスクロールできない
   * @note
   * FullscreenModalのopenプロパティはアイテム詳細ページではDRM初期化のため常にtrueにする必要があるが、このコンポーネントを表示するとbody要素のスタイルにoverflow:hiddenが適用される。
   * コメント一覧などの他のページでスクロールを適用するために、アイテム詳細ページかどうかによってopenプロパティを制御する
   */
  isDrm?: boolean
}

/**
 * アイテムの全画面表示ビュワー
 */
export const ThumbnailFullScreenModal: Organism<ThumbnailFullScreenModalProps> =
  ({
    open,
    thumbnail,
    resource,
    onClose,
    isFlipped,
    showMultipleImage,
    images,
    isDrm,
  }) => {
    usePrivacyScreen(open)

    const { t } = useTranslation('ownedItemDetail')

    if (showMultipleImage) {
      return (
        <FullscreenModal onClose={onClose} open>
          <Fade>
            <MultipleImageFullScreenDisplayView
              images={images}
              onClose={onClose}
            />
          </Fade>
        </FullscreenModal>
      )
    }

    return (
      <>
        {open && (
          <Head>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0, viewport-fit=cover, minimum-scale=1.0, maximum-scale=2.0"
            />
          </Head>
        )}
        <FullscreenModal
          onClose={onClose}
          // NOTE: DRMの読み込みにrefを参照する必要があるため、モーダル内部のDOM要素は常に出力し、スタイルで表示を制御する
          sx={{ display: open ? 'block' : 'none' }}
          open={isDrm ? true : open}
        >
          <Fade>
            {thumbnail || resource ? (
              <ImageFullScreenDisplayView
                thumbnail={thumbnail}
                resource={resource}
                onClose={onClose}
                open={open}
                isFlipped={isFlipped}
              />
            ) : (
              <Typography variant="body1" sx={{ textAlign: 'center' }}>
                {t('itemNotFound')}
              </Typography>
            )}
          </Fade>
        </FullscreenModal>
      </>
    )
  }

type ItemFullScreenDisplayViewProps = {
  thumbnail?: {
    front?: Maybe<ResourceFragment> | undefined
    back?: Maybe<ResourceFragment> | undefined
  }
  resource?: Maybe<ResourceFragment> | undefined
  open: boolean
  isFlipped?: boolean
  onClose: () => void
}

const ImageFullScreenDisplayView: Organism<ItemFullScreenDisplayViewProps> = ({
  thumbnail,
  resource,
  open,
  isFlipped,
  onClose,
}) => (
  <Stack
    flex={1}
    alignItems="center"
    justifyContent="center"
    direction="column"
    sx={{ height: '100vh', bgcolor: 'background.default' }}
  >
    <Stack
      direction="column"
      flex={1}
      sx={{
        position: 'absolute',
        top: styles.safeAreaTop,
        left: 0,
        right: 0,
        bottom: styles.safeAreaBottom,
        height: `calc(100% - ${styles.safeAreaTop} - $${styles.safeAreaBottom})`,
      }}
    >
      <Header
        left={
          <IconButton.Close edge="start" onClick={onClose} bgcolor="grey.50" />
        }
        sx={{ height: 0, zIndex: 100 }}
      />
      <Stack flex={1} height="100%" justifyContent="center">
        {resource && (
          <ImagePlayer
            open={open}
            resource={resource}
            width="100%"
            height="100%"
          />
        )}
        {thumbnail && (
          <>
            {/* 表面のプレイヤー */}
            <Box sx={{ display: isFlipped ? 'none' : 'block' }}>
              {thumbnail?.front && (
                <ImagePlayer
                  open={open}
                  resource={thumbnail?.front}
                  width="100%"
                  height="100%"
                />
              )}
            </Box>
            {/* 裏面のプレイヤー */}
            <Box sx={{ display: isFlipped ? 'block' : 'none' }}>
              {thumbnail?.back && (
                <ImagePlayer
                  open={open}
                  resource={thumbnail?.back}
                  width="100%"
                  height="100%"
                />
              )}
            </Box>
          </>
        )}
      </Stack>
    </Stack>
  </Stack>
)

export const usePrivacyScreen = (enable: boolean) => {
  useEffect(() => {
    enable ? Privacy.enablePrivacyScreen() : Privacy.disablePrivacyScreen()
  })
}

type MultipleImageFullScreenDisplayViewProps = {
  images: ThumbnailFullScreenModalProps['images']
  onClose: ThumbnailFullScreenModalProps['onClose']
}
const MultipleImageFullScreenDisplayView: Organism<MultipleImageFullScreenDisplayViewProps> =
  ({ images, onClose }) => {
    const { t } = useTranslation('ownedItemDetail')
    const [index, setIndex] = useState<number>(0)
    if (!images?.length) {
      return (
        <Typography variant="body1" sx={{ textAlign: 'center' }}>
          {t('itemNotFound')}
        </Typography>
      )
    }
    return (
      <BaseLayout
        header={{
          left: (
            <IconButton.Close
              edge="start"
              onClick={onClose}
              bgcolor="grey.50"
            />
          ),
        }}
      >
        <Stack flex={1} height="100%">
          <Stack flex={1} alignItems="center" justifyContent="center">
            <Avatar
              variant="square"
              src={images[index]?.mediumUri ?? ''}
              sx={{ width: '100%', height: 375 }}
            />
            <Stack
              width="100%"
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              sx={{ px: 5, py: 2 }}
            >
              <Typography variant="caption">
                {images[index]?.caption}
              </Typography>
              <Box
                sx={{
                  bgcolor: 'grey.900',
                  borderRadius: '40px',
                  p: '3px 6px',
                }}
              >
                <Typography variant="subtitle2" color="white">
                  {index + 1}/{images.length}
                </Typography>
              </Box>
            </Stack>
          </Stack>
          <Stack
            flexWrap="wrap"
            sx={{
              px: 5,
              pb: 5,
              overflowY: 'auto',
              ...styleSets.hideScrollbar(),
            }}
          >
            <Stack
              direction="row"
              flexWrap="nowrap"
              overflow="hidden"
              spacing={2}
            >
              {images.map((img, idx) => (
                <ButtonBase
                  onClick={() => setIndex(idx)}
                  sx={{
                    border: theme =>
                      index === idx
                        ? `2px solid ${theme.palette.primary.main}`
                        : 'none',
                  }}
                >
                  <Avatar
                    variant="square"
                    src={img.mediumUri ?? ''}
                    sx={{
                      width: index === idx ? 38 : 40,
                      height: index === idx ? 38 : 40,
                    }}
                  />
                </ButtonBase>
              ))}
            </Stack>
          </Stack>
        </Stack>
      </BaseLayout>
    )
  }
